import { useState, useEffect, useCallback } from 'react'
import axios from '../../lib/axios'
import {
  TrendingUp, TrendingDown, Mail, MousePointer, Reply, AlertCircle,
  Download, BarChart3, PieChart, Activity, Users, Shield, Globe,
  RefreshCw, ChevronRight, Eye, CheckCircle, Target, Layers,
  ArrowUpRight, Clock, Inbox, Star, Zap, Sparkles, Brain,
  AlertTriangle, Lightbulb, ThumbsUp, ThumbsDown, Gauge,
  MessageCircle, Send, Timer, Type, Award, Crosshair
} from 'lucide-react'
import '../Pages.css'

// ─── Helpers ──────────────────────────────────────────────────
const fmt = (n) => (n ?? 0).toLocaleString()
const pct = (n) => `${n ?? 0}%`
const rateColor = (rate) => rate >= 50 ? '#22c55e' : rate >= 25 ? '#f59e0b' : '#ef4444'
const rateClass = (rate) => rate >= 50 ? 'good' : rate >= 25 ? 'medium' : 'low'

function Reports() {
  const [activeTab, setActiveTab] = useState('overview')
  const [dateRange, setDateRange] = useState('30d')
  const [loading, setLoading] = useState(true)

  // Data states
  const [dashboard, setDashboard] = useState(null)
  const [engagement, setEngagement] = useState(null)
  const [accountHealth, setAccountHealth] = useState(null)
  const [replyAnalytics, setReplyAnalytics] = useState(null)
  const [topContacts, setTopContacts] = useState(null)
  const [domainData, setDomainData] = useState(null)
  const [queueStatus, setQueueStatus] = useState(null)
  const [aiInsights, setAiInsights] = useState(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState(null)
  const [aiSuggestions, setAiSuggestions] = useState(null)
  const [sugLoading, setSugLoading] = useState(false)
  const [sendTimes, setSendTimes] = useState(null)
  const [sendTimesLoading, setSendTimesLoading] = useState(false)
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const [subjectInput, setSubjectInput] = useState('')
  const [subjectScore, setSubjectScore] = useState(null)
  const [subjectLoading, setSubjectLoading] = useState(false)
  const [segments, setSegments] = useState(null)
  const [segLoading, setSegLoading] = useState(false)

  const daysMap = { '7d': 7, '30d': 30, '90d': 90, '12m': 365 }

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const days = daysMap[dateRange]
      const [dashRes, engRes, acctRes, replyRes, contactsRes, domainRes, queueRes] = await Promise.allSettled([
        axios.get('reports/dashboard'),
        axios.get(`reports/engagement-over-time?period=daily&days=${days}`),
        axios.get('reports/account-health'),
        axios.get('reports/reply-analytics'),
        axios.get('reports/top-contacts?limit=20'),
        axios.get('reports/deliverability-by-domain?limit=15'),
        axios.get('reports/queue-status'),
      ])
      if (dashRes.status === 'fulfilled') setDashboard(dashRes.value.data)
      if (engRes.status === 'fulfilled') setEngagement(engRes.value.data)
      if (acctRes.status === 'fulfilled') setAccountHealth(acctRes.value.data)
      if (replyRes.status === 'fulfilled') setReplyAnalytics(replyRes.value.data)
      if (contactsRes.status === 'fulfilled') setTopContacts(contactsRes.value.data)
      if (domainRes.status === 'fulfilled') setDomainData(domainRes.value.data)
      if (queueRes.status === 'fulfilled') setQueueStatus(queueRes.value.data)
    } catch (err) {
      console.error('Error loading reports:', err)
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => { fetchAll() }, [fetchAll])

  const fetchAiInsights = useCallback(async () => {
    setAiLoading(true)
    setAiError(null)
    try {
      const res = await axios.get(`reports/ai-insights?date_range=${dateRange}`)
      if (res.data.error && !res.data.insights) {
        setAiError(res.data.error)
      } else {
        setAiInsights(res.data)
      }
    } catch (err) {
      setAiError(err.response?.data?.detail || 'Failed to generate AI insights')
    } finally {
      setAiLoading(false)
    }
  }, [dateRange])

  const fetchAiSuggestions = useCallback(async () => {
    setSugLoading(true)
    try {
      const res = await axios.get(`reports/ai-suggestions?date_range=${dateRange}`)
      if (res.data.suggestions) {
        setAiSuggestions(res.data)
      }
    } catch (err) {
      console.error('AI suggestions error:', err)
    } finally {
      setSugLoading(false)
    }
  }, [dateRange])

  const fetchSendTimes = useCallback(async () => {
    setSendTimesLoading(true)
    try {
      const res = await axios.get('reports/best-send-times')
      setSendTimes(res.data)
    } catch (err) {
      console.error('Send times error:', err)
    } finally {
      setSendTimesLoading(false)
    }
  }, [])

  const handleAskAi = async () => {
    if (!chatInput.trim() || chatLoading) return
    const question = chatInput.trim()
    setChatMessages(prev => [...prev, { role: 'user', content: question }])
    setChatInput('')
    setChatLoading(true)
    try {
      const res = await axios.post('reports/ask-ai', { question })
      setChatMessages(prev => [...prev, { role: 'ai', content: res.data.answer }])
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'ai', content: 'Sorry, something went wrong. Please try again.' }])
    } finally {
      setChatLoading(false)
    }
  }

  const handleExport = async (type) => {
    try {
      const res = await axios.get(`reports/export/${type}`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `report_${type}_${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  const scoreSubjectLine = async () => {
    if (!subjectInput.trim() || subjectLoading) return
    setSubjectLoading(true)
    setSubjectScore(null)
    try {
      const res = await axios.post('reports/score-subject-line', { subject_line: subjectInput.trim() })
      setSubjectScore(res.data)
    } catch (err) {
      console.error('Subject scoring failed:', err)
    } finally {
      setSubjectLoading(false)
    }
  }

  const fetchSegments = async () => {
    if (segLoading) return
    setSegLoading(true)
    setSegments(null)
    try {
      const res = await axios.get('reports/smart-segments')
      setSegments(res.data)
    } catch (err) {
      console.error('Segments fetch failed:', err)
    } finally {
      setSegLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="page animate-fade-in">
        <div className="page-header">
          <div className="header-info"><h2>Reports</h2><p>Loading your analytics...</p></div>
        </div>
        <div className="card">
          <div style={{ padding: '4rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="animate-spin rounded-full h-12 w-12" style={{ width: 48, height: 48, border: '3px solid var(--color-border)', borderTopColor: 'var(--color-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          </div>
        </div>
      </div>
    )
  }

  const kpis = dashboard?.kpis || {}
  const trend = dashboard?.daily_trend || []
  const bestCampaign = dashboard?.best_campaign
  const worstCampaign = dashboard?.worst_campaign
  const statusBreakdown = dashboard?.status_breakdown || {}

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'ai-insights', label: 'AI Insights', icon: Sparkles },
    { id: 'send-times', label: 'Send Times', icon: Timer },
    { id: 'ask-ai', label: 'Ask AI', icon: MessageCircle },
    { id: 'subject-scorer', label: 'Subject Scorer', icon: Type },
    { id: 'smart-segments', label: 'Smart Segments', icon: Crosshair },
    { id: 'engagement', label: 'Engagement', icon: Activity },
    { id: 'accounts', label: 'Account Health', icon: Shield },
    { id: 'replies', label: 'Replies', icon: Reply },
    { id: 'contacts', label: 'Top Contacts', icon: Users },
    { id: 'domains', label: 'Domains', icon: Globe },
  ]

  return (
    <div className="page reports-page animate-fade-in">
      {/* ── Header ── */}
      <div className="page-header">
        <div className="header-info">
          <h2>Reports</h2>
          <p>Track your email performance and analytics across all campaigns</p>
        </div>
        <div className="header-actions">
          <div className="date-selector">
            {['7d', '30d', '90d', '12m'].map(r => (
              <button key={r} className={`date-btn ${dateRange === r ? 'active' : ''}`} onClick={() => setDateRange(r)}>
                {r === '7d' ? '7 Days' : r === '30d' ? '30 Days' : r === '90d' ? '90 Days' : '12 Months'}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost" onClick={fetchAll}><RefreshCw size={18} /><span>Refresh</span></button>
          <button className="btn btn-ghost" onClick={() => handleExport('campaign_stats')}><Download size={18} /><span>Export</span></button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="stats-grid five-col">
        {[
          { label: 'Total Sent', value: fmt(kpis.total_sent), icon: Mail, color: '#6366f1' },
          { label: 'Open Rate', value: pct(kpis.avg_open_rate), icon: Eye, color: '#22c55e' },
          { label: 'Click Rate', value: pct(kpis.avg_click_rate), icon: MousePointer, color: '#3b82f6' },
          { label: 'Reply Rate', value: fmt(kpis.total_replies), subtext: 'replies', icon: Reply, color: '#a855f7' },
          { label: 'Bounce Rate', value: pct(kpis.avg_bounce_rate), icon: AlertCircle, color: '#f59e0b' },
        ].map((stat, i) => (
          <div key={i} className="stat-card">
            <div className="stat-icon" style={{ background: `${stat.color}15`, color: stat.color }}><stat.icon size={24} /></div>
            <div className="stat-content">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
            {stat.subtext && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: 500 }}>{stat.subtext}</span>}
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div className="filter-tabs" style={{ marginBottom: '1.25rem' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`filter-tab ${activeTab === tab.id ? 'active' : ''}`}>
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════ */}
      {/* TAB: Overview                                  */}
      {/* ══════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <>
          {/* Send Trend Chart + Delivery Donut */}
          <div className="charts-grid">
            <div className="card chart-card">
              <div className="card-header"><h3><BarChart3 size={20} /> Send Trend ({dateRange})</h3></div>
              <div className="chart-container">
                {trend.length > 0 ? (
                  <>
                    <div className="bar-chart">
                      {(() => {
                        const maxSent = Math.max(...trend.map(d => d.sent), 1)
                        const displayed = trend.slice(-14)
                        return displayed.map((day, i) => (
                          <div key={i} className="bar-group" title={`${day.date}\nSent: ${day.sent} | Opens: ${day.opens} | Clicks: ${day.clicks}`}>
                            <div className="bars">
                              <div className="bar sent" style={{ height: `${(day.sent / maxSent) * 100}%` }} />
                              <div className="bar opened" style={{ height: `${(day.opens / maxSent) * 100}%` }} />
                              <div className="bar clicked" style={{ height: `${(day.clicks / maxSent) * 100}%` }} />
                            </div>
                            <span className="bar-label">{day.date.slice(5)}</span>
                          </div>
                        ))
                      })()}
                    </div>
                    <div className="chart-legend">
                      <span className="legend-item"><span className="dot sent"></span> Sent</span>
                      <span className="legend-item"><span className="dot opened"></span> Opened</span>
                      <span className="legend-item"><span className="dot clicked"></span> Clicked</span>
                    </div>
                  </>
                ) : (
                  <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    <Mail size={40} style={{ margin: '0 auto 0.75rem', opacity: 0.4 }} />
                    <p>No send data in this period</p>
                  </div>
                )}
              </div>
            </div>

            <div className="card chart-card">
              <div className="card-header"><h3><PieChart size={20} /> Delivery Breakdown</h3></div>
              <div className="donut-chart-container">
                {(() => {
                  const sent = kpis.total_sent || 0
                  const delivered = kpis.total_delivered || 0
                  const bounced = kpis.total_bounced || 0
                  const deliveryPct = sent > 0 ? ((delivered / sent) * 100).toFixed(1) : 0
                  const circumference = 2 * Math.PI * 40
                  const offset = sent > 0 ? circumference * (1 - delivered / sent) : circumference
                  return (
                    <>
                      <div className="donut-chart">
                        <svg viewBox="0 0 100 100">
                          <circle cx="50" cy="50" r="40" fill="none" stroke="var(--color-border)" strokeWidth="12" />
                          <circle cx="50" cy="50" r="40" fill="none" stroke="#22c55e" strokeWidth="12"
                            strokeDasharray={circumference} strokeDashoffset={offset}
                            transform="rotate(-90 50 50)" style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
                        </svg>
                        <div className="donut-center">
                          <span className="donut-value">{deliveryPct}%</span>
                          <span className="donut-label">Delivered</span>
                        </div>
                      </div>
                      <div className="donut-legend">
                        <div className="legend-row"><span className="dot" style={{ background: '#22c55e' }}></span><span className="label">Delivered</span><span className="value">{fmt(delivered)}</span></div>
                        <div className="legend-row"><span className="dot" style={{ background: '#ef4444' }}></span><span className="label">Bounced</span><span className="value">{fmt(bounced)}</span></div>
                        <div className="legend-row"><span className="dot" style={{ background: '#a855f7' }}></span><span className="label">Replies</span><span className="value">{fmt(kpis.total_replies)}</span></div>
                        <div className="legend-row"><span className="dot" style={{ background: '#3b82f6' }}></span><span className="label">Opened</span><span className="value">{fmt(kpis.total_opens)}</span></div>
                      </div>
                    </>
                  )
                })()}
              </div>
            </div>
          </div>

          {/* Campaign Status + Best/Worst */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
            {/* Status Breakdown */}
            <div className="card">
              <div style={{ padding: '1.5rem' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Layers size={16} /> Campaign Status
                </h4>
                {Object.entries(statusBreakdown).length > 0 ? Object.entries(statusBreakdown).map(([status, count]) => (
                  <div key={status} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.625rem 0', borderBottom: '1px solid var(--color-border-light)' }}>
                    <span className={`status-badge ${status}`}>{status}</span>
                    <span style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>{count}</span>
                  </div>
                )) : (
                  <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>No campaigns yet</p>
                )}
              </div>
            </div>

            {/* Best Campaign */}
            <div className="card" style={{ background: 'rgba(34, 197, 94, 0.03)', border: '1px solid rgba(34, 197, 94, 0.15)' }}>
              <div style={{ padding: '1.5rem' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#22c55e', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingUp size={16} /> Best Performing
                </h4>
                {bestCampaign ? (
                  <>
                    <p style={{ fontWeight: 700, color: 'var(--color-text-primary)', fontSize: '1.1rem', marginBottom: '0.75rem' }}>{bestCampaign.name}</p>
                    <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.8rem' }}>
                      <div><span style={{ color: 'var(--color-text-secondary)' }}>Open Rate</span><p style={{ fontWeight: 700, color: '#22c55e', fontSize: '1.25rem' }}>{bestCampaign.open_rate}%</p></div>
                      <div><span style={{ color: 'var(--color-text-secondary)' }}>Click Rate</span><p style={{ fontWeight: 700, color: '#3b82f6', fontSize: '1.25rem' }}>{bestCampaign.click_rate}%</p></div>
                      <div><span style={{ color: 'var(--color-text-secondary)' }}>Sent</span><p style={{ fontWeight: 700, color: 'var(--color-text-primary)', fontSize: '1.25rem' }}>{fmt(bestCampaign.sent)}</p></div>
                    </div>
                  </>
                ) : <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Need 10+ emails sent to rank</p>}
              </div>
            </div>

            {/* Worst Campaign */}
            <div className="card" style={{ background: 'rgba(239, 68, 68, 0.03)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
              <div style={{ padding: '1.5rem' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#ef4444', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingDown size={16} /> Needs Improvement
                </h4>
                {worstCampaign && worstCampaign.name !== bestCampaign?.name ? (
                  <>
                    <p style={{ fontWeight: 700, color: 'var(--color-text-primary)', fontSize: '1.1rem', marginBottom: '0.75rem' }}>{worstCampaign.name}</p>
                    <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.8rem' }}>
                      <div><span style={{ color: 'var(--color-text-secondary)' }}>Open Rate</span><p style={{ fontWeight: 700, color: '#ef4444', fontSize: '1.25rem' }}>{worstCampaign.open_rate}%</p></div>
                      <div><span style={{ color: 'var(--color-text-secondary)' }}>Click Rate</span><p style={{ fontWeight: 700, color: '#f59e0b', fontSize: '1.25rem' }}>{worstCampaign.click_rate}%</p></div>
                      <div><span style={{ color: 'var(--color-text-secondary)' }}>Sent</span><p style={{ fontWeight: 700, color: 'var(--color-text-primary)', fontSize: '1.25rem' }}>{fmt(worstCampaign.sent)}</p></div>
                    </div>
                  </>
                ) : <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Not enough data to compare</p>}
              </div>
            </div>
          </div>

          {/* Queue Status */}
          {queueStatus && (
            <div className="card" style={{ marginBottom: '1.25rem' }}>
              <div className="card-header"><h3><Inbox size={20} /> Email Queue</h3></div>
              <div style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.5rem' }}>
                  {['pending', 'sent', 'failed', 'cancelled'].map(s => (
                    <div key={s} style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '1.75rem', fontWeight: 800, color: s === 'pending' ? '#f59e0b' : s === 'sent' ? '#22c55e' : s === 'failed' ? '#ef4444' : '#94a3b8' }}>
                        {fmt(queueStatus.breakdown?.[s] || 0)}
                      </p>
                      <p style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)', textTransform: 'capitalize', fontWeight: 500 }}>{s}</p>
                    </div>
                  ))}
                </div>
                {queueStatus.upcoming?.length > 0 && (
                  <>
                    <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '0.75rem' }}>Next Scheduled</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {queueStatus.upcoming.slice(0, 5).map(eq => (
                        <div key={eq.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'var(--color-bg-primary)', borderRadius: '8px', fontSize: '0.8rem' }}>
                          <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{eq.recipient_email}</span>
                          <span style={{ color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                            <Clock size={13} />
                            {eq.scheduled_time ? new Date(eq.scheduled_time).toLocaleString() : '-'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* TAB: AI Insights                                */}
      {/* ══════════════════════════════════════════════ */}
      {activeTab === 'ai-insights' && (
        <div>
          {/* Generate Button / Loading State */}
          {!aiInsights && !aiLoading && (
            <div className="card" style={{ marginBottom: '1.25rem' }}>
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <div style={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(99, 102, 241, 0.1))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 1.25rem'
                }}>
                  <Brain size={36} style={{ color: '#8b5cf6' }} />
                </div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '0.5rem' }}>
                  AI-Powered Report Analysis
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginBottom: '1.5rem', maxWidth: 480, margin: '0 auto 1.5rem' }}>
                  Get an executive summary, key insights, actionable recommendations, and a health score — all generated by AI from your {dateRange === '7d' ? '7-day' : dateRange === '30d' ? '30-day' : dateRange === '90d' ? '90-day' : '12-month'} data.
                </p>
                <button className="btn btn-primary" onClick={fetchAiInsights} style={{
                  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  border: 'none', padding: '0.75rem 2rem', fontSize: '0.9rem',
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem'
                }}>
                  <Sparkles size={18} /> Generate AI Insights
                </button>
                {aiError && (
                  <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '1rem' }}>{aiError}</p>
                )}
              </div>
            </div>
          )}

          {/* Loading */}
          {aiLoading && (
            <div className="card" style={{ marginBottom: '1.25rem' }}>
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <div style={{
                  width: 48, height: 48, border: '3px solid var(--color-border)',
                  borderTopColor: '#8b5cf6', borderRadius: '50%',
                  animation: 'spin 1s linear infinite', margin: '0 auto 1rem'
                }} />
                <p style={{ fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '0.25rem' }}>Analyzing your data with AI...</p>
                <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>This may take 10-15 seconds</p>
              </div>
            </div>
          )}

          {/* AI Insights Content */}
          {aiInsights?.insights && !aiLoading && (() => {
            const ins = aiInsights.insights
            const insightIconMap = {
              positive: { icon: ThumbsUp, color: '#22c55e', bg: 'rgba(34, 197, 94, 0.08)' },
              warning: { icon: AlertTriangle, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)' },
              critical: { icon: AlertCircle, color: '#ef4444', bg: 'rgba(239, 68, 68, 0.08)' },
              opportunity: { icon: Lightbulb, color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.08)' },
            }
            const priorityColor = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' }
            const verdictStyle = {
              strong: { color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)' },
              average: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
              weak: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
            }

            return (
              <>
                {/* Health Score + Executive Summary */}
                <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                  {/* Health Score Card */}
                  <div className="card" style={{
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.04), rgba(99, 102, 241, 0.04))',
                    border: '1px solid rgba(139, 92, 246, 0.15)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '2rem'
                  }}>
                    <div style={{ position: 'relative', width: 120, height: 120, marginBottom: '1rem' }}>
                      <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
                        <circle cx="50" cy="50" r="42" fill="none" stroke="var(--color-border)" strokeWidth="8" />
                        <circle cx="50" cy="50" r="42" fill="none"
                          stroke={ins.overall_health_score >= 70 ? '#22c55e' : ins.overall_health_score >= 40 ? '#f59e0b' : '#ef4444'}
                          strokeWidth="8"
                          strokeDasharray={2 * Math.PI * 42}
                          strokeDashoffset={2 * Math.PI * 42 * (1 - (ins.overall_health_score || 0) / 100)}
                          strokeLinecap="round"
                          style={{ transition: 'stroke-dashoffset 1s ease' }}
                        />
                      </svg>
                      <div style={{
                        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                        textAlign: 'center'
                      }}>
                        <span style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--color-text-primary)' }}>
                          {ins.overall_health_score}
                        </span>
                      </div>
                    </div>
                    <span style={{
                      fontSize: '0.875rem', fontWeight: 700,
                      color: ins.overall_health_score >= 70 ? '#22c55e' : ins.overall_health_score >= 40 ? '#f59e0b' : '#ef4444'
                    }}>
                      {ins.health_label}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>Overall Health Score</span>
                  </div>

                  {/* Executive Summary */}
                  <div className="card">
                    <div style={{ padding: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                        <Sparkles size={18} style={{ color: '#8b5cf6' }} />
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>Executive Summary</h3>
                        <button className="btn btn-ghost btn-sm" onClick={fetchAiInsights} style={{ marginLeft: 'auto' }}>
                          <RefreshCw size={14} /> Regenerate
                        </button>
                      </div>
                      <p style={{ fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--color-text-secondary)', marginBottom: '1.25rem' }}>
                        {ins.executive_summary}
                      </p>

                      {/* Key Metrics Analysis */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        {ins.key_metrics_analysis && Object.entries(ins.key_metrics_analysis).map(([key, value]) => (
                          <div key={key} style={{
                            padding: '0.75rem', borderRadius: '8px',
                            background: 'var(--color-bg-primary)', fontSize: '0.8rem'
                          }}>
                            <span style={{ fontWeight: 600, color: 'var(--color-text-primary)', textTransform: 'capitalize', display: 'block', marginBottom: '0.25rem' }}>
                              {key.replace(/_/g, ' ')}
                            </span>
                            <span style={{ color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Insights */}
                {ins.top_insights?.length > 0 && (
                  <div className="card" style={{ marginBottom: '1.25rem' }}>
                    <div className="card-header">
                      <h3><Brain size={20} /> Key Insights</h3>
                    </div>
                    <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {ins.top_insights.map((insight, i) => {
                        const cfg = insightIconMap[insight.type] || insightIconMap.opportunity
                        const Icon = cfg.icon
                        return (
                          <div key={i} style={{
                            display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                            padding: '1rem', borderRadius: '10px', background: cfg.bg,
                            border: `1px solid ${cfg.color}22`
                          }}>
                            <div style={{
                              width: 36, height: 36, borderRadius: '8px',
                              background: `${cfg.color}15`, color: cfg.color,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}>
                              <Icon size={18} />
                            </div>
                            <div>
                              <p style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--color-text-primary)', marginBottom: '0.25rem' }}>
                                {insight.title}
                              </p>
                              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                                {insight.detail}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Recommendations + Campaign Highlights */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                  {/* Recommendations */}
                  {ins.recommendations?.length > 0 && (
                    <div className="card">
                      <div className="card-header">
                        <h3><Lightbulb size={20} /> Recommendations</h3>
                      </div>
                      <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {ins.recommendations.map((rec, i) => (
                          <div key={i} style={{
                            padding: '1rem', borderRadius: '10px',
                            background: 'var(--color-bg-primary)',
                            borderLeft: `3px solid ${priorityColor[rec.priority] || '#6366f1'}`
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                              <span style={{
                                fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
                                padding: '2px 6px', borderRadius: '4px',
                                background: `${priorityColor[rec.priority] || '#6366f1'}15`,
                                color: priorityColor[rec.priority] || '#6366f1'
                              }}>
                                {rec.priority}
                              </span>
                            </div>
                            <p style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--color-text-primary)', marginBottom: '0.25rem' }}>
                              {rec.action}
                            </p>
                            <p style={{ fontSize: '0.775rem', color: 'var(--color-text-muted)' }}>
                              {rec.expected_impact}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Campaign Highlights */}
                  {ins.campaign_highlights?.length > 0 && (
                    <div className="card">
                      <div className="card-header">
                        <h3><Target size={20} /> Campaign Highlights</h3>
                      </div>
                      <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {ins.campaign_highlights.map((ch, i) => {
                          const vs = verdictStyle[ch.verdict] || verdictStyle.average
                          return (
                            <div key={i} style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '0.875rem 1rem', borderRadius: '10px',
                              background: 'var(--color-bg-primary)',
                              borderBottom: '1px solid var(--color-border-light)'
                            }}>
                              <div style={{ flex: 1 }}>
                                <p style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--color-text-primary)', marginBottom: '0.2rem' }}>
                                  {ch.campaign_name}
                                </p>
                                <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{ch.reason}</p>
                              </div>
                              <span style={{
                                fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
                                padding: '4px 10px', borderRadius: '6px',
                                background: vs.bg, color: vs.color, flexShrink: 0
                              }}>
                                {ch.verdict}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Generated timestamp */}
                <div style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  <Sparkles size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.375rem' }} />
                  Generated by AI ({aiInsights.ai_model}) at {new Date(aiInsights.generated_at).toLocaleString()}
                  {' · '}Analyzing {dateRange === '7d' ? '7 days' : dateRange === '30d' ? '30 days' : dateRange === '90d' ? '90 days' : '12 months'} of data
                </div>
              </>
            )
          })()}

          {/* ── AI Suggestions (Web Search + Past Data) ── */}
          {aiInsights?.insights && !aiLoading && (
            <div style={{ marginTop: '1.5rem', borderTop: '2px solid var(--color-border)', paddingTop: '1.5rem' }}>
              {!aiSuggestions && !sugLoading && (
                <div className="card">
                  <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(16, 185, 129, 0.1))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 1rem'
                    }}>
                      <Globe size={28} style={{ color: '#3b82f6' }} />
                    </div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '0.375rem' }}>
                      Real-Time AI Suggestions
                    </h3>
                    <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '1.25rem', maxWidth: 440, margin: '0 auto 1.25rem' }}>
                      AI analyzes your past campaigns + searches the web for latest email marketing trends to give you specific, data-backed recommendations.
                    </p>
                    <button className="btn btn-primary" onClick={fetchAiSuggestions} style={{
                      background: 'linear-gradient(135deg, #3b82f6, #10b981)',
                      border: 'none', padding: '0.625rem 1.5rem', fontSize: '0.85rem',
                      display: 'inline-flex', alignItems: 'center', gap: '0.5rem'
                    }}>
                      <Globe size={16} /> Get Smart Suggestions
                    </button>
                  </div>
                </div>
              )}

              {sugLoading && (
                <div className="card">
                  <div style={{ padding: '2.5rem', textAlign: 'center' }}>
                    <div style={{
                      width: 48, height: 48, border: '3px solid var(--color-border)',
                      borderTopColor: '#3b82f6', borderRadius: '50%',
                      animation: 'spin 1s linear infinite', margin: '0 auto 1rem'
                    }} />
                    <p style={{ fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '0.25rem' }}>Searching the web + analyzing your campaigns...</p>
                    <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>This may take 15-30 seconds as AI searches for latest trends</p>
                  </div>
                </div>
              )}

              {aiSuggestions?.suggestions && !sugLoading && (() => {
                const sug = aiSuggestions.suggestions
                const categoryIcons = {
                  subject_lines: { icon: Mail, color: '#8b5cf6' },
                  send_timing: { icon: Clock, color: '#f59e0b' },
                  deliverability: { icon: Shield, color: '#22c55e' },
                  content: { icon: Layers, color: '#3b82f6' },
                  audience: { icon: Users, color: '#ec4899' },
                  reply_optimization: { icon: Reply, color: '#a855f7' },
                  sequence_strategy: { icon: Zap, color: '#6366f1' },
                }
                const impactColor = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' }

                return (
                  <>
                    {/* Overall Strategy */}
                    {sug.overall_strategy && (
                      <div className="card" style={{
                        marginBottom: '1.25rem',
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.03), rgba(16, 185, 129, 0.03))',
                        border: '1px solid rgba(59, 130, 246, 0.12)'
                      }}>
                        <div style={{ padding: '1.25rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                            <Globe size={18} style={{ color: '#3b82f6' }} />
                            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>Strategic Direction</h3>
                            <span style={{ fontSize: '0.6rem', padding: '2px 8px', borderRadius: '4px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 700 }}>WEB + DATA</span>
                            <button className="btn btn-ghost btn-sm" onClick={fetchAiSuggestions} style={{ marginLeft: 'auto' }}>
                              <RefreshCw size={14} /> Refresh
                            </button>
                          </div>
                          <p style={{ fontSize: '0.875rem', lineHeight: 1.7, color: 'var(--color-text-secondary)' }}>{sug.overall_strategy}</p>
                        </div>
                      </div>
                    )}

                    {/* Suggestions Grid */}
                    {sug.suggestions?.length > 0 && (
                      <div className="card" style={{ marginBottom: '1.25rem' }}>
                        <div className="card-header">
                          <h3><Lightbulb size={20} /> Actionable Suggestions</h3>
                          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{sug.suggestions.length} suggestions</span>
                        </div>
                        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {sug.suggestions.map((s, i) => {
                            const cat = categoryIcons[s.category] || { icon: Lightbulb, color: '#6366f1' }
                            const CatIcon = cat.icon
                            return (
                              <div key={i} style={{
                                padding: '1rem', borderRadius: '10px',
                                background: 'var(--color-bg-primary)',
                                borderLeft: `3px solid ${cat.color}`,
                                display: 'flex', gap: '0.75rem'
                              }}>
                                <div style={{
                                  width: 36, height: 36, borderRadius: '8px',
                                  background: `${cat.color}12`, color: cat.color,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                  <CatIcon size={18} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--color-text-primary)' }}>{s.title}</span>
                                    <span style={{
                                      fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
                                      padding: '2px 6px', borderRadius: '4px',
                                      background: `${impactColor[s.impact_estimate] || '#6366f1'}12`,
                                      color: impactColor[s.impact_estimate] || '#6366f1'
                                    }}>
                                      {s.impact_estimate} impact
                                    </span>
                                    {s.quick_win && (
                                      <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}>
                                        QUICK WIN
                                      </span>
                                    )}
                                    <span style={{
                                      fontSize: '0.6rem', fontWeight: 600, padding: '2px 6px', borderRadius: '4px',
                                      background: s.based_on === 'web_research' ? 'rgba(59, 130, 246, 0.1)' : s.based_on === 'both' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                      color: s.based_on === 'web_research' ? '#3b82f6' : s.based_on === 'both' ? '#8b5cf6' : '#f59e0b'
                                    }}>
                                      {s.based_on === 'web_research' ? 'WEB' : s.based_on === 'both' ? 'WEB + DATA' : 'YOUR DATA'}
                                    </span>
                                  </div>
                                  <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', lineHeight: 1.5, marginBottom: '0.25rem' }}>{s.detail}</p>
                                  <p style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>{s.source_insight}</p>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                      {/* Subject Line Suggestions */}
                      {sug.subject_line_suggestions?.length > 0 && (
                        <div className="card">
                          <div className="card-header"><h3><Mail size={18} /> Subject Line Ideas</h3></div>
                          <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {sug.subject_line_suggestions.map((sl, i) => (
                              <div key={i} style={{ padding: '0.75rem', borderRadius: '8px', background: 'var(--color-bg-primary)' }}>
                                {sl.current_pattern && (
                                  <p style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: '0.375rem' }}>
                                    Current: <span style={{ textDecoration: 'line-through' }}>{sl.current_pattern}</span>
                                  </p>
                                )}
                                <p style={{ fontWeight: 700, fontSize: '0.85rem', color: '#8b5cf6', marginBottom: '0.25rem' }}>
                                  "{sl.suggested}"
                                </p>
                                <p style={{ fontSize: '0.725rem', color: 'var(--color-text-secondary)' }}>{sl.why}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Best Send Times */}
                      {sug.best_send_times && (
                        <div className="card">
                          <div className="card-header"><h3><Clock size={18} /> Best Send Times</h3></div>
                          <div style={{ padding: '1.25rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                              {sug.best_send_times.recommended_days?.map((d, i) => (
                                <span key={i} style={{
                                  padding: '6px 14px', borderRadius: '8px', fontWeight: 700, fontSize: '0.8rem',
                                  background: 'rgba(34, 197, 94, 0.08)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.2)'
                                }}>
                                  {d}
                                </span>
                              ))}
                            </div>
                            <div style={{
                              padding: '0.75rem', borderRadius: '8px',
                              background: 'rgba(99, 102, 241, 0.05)', border: '1px solid rgba(99, 102, 241, 0.12)',
                              marginBottom: '0.75rem'
                            }}>
                              <p style={{ fontWeight: 700, fontSize: '1.1rem', color: '#6366f1', marginBottom: '0.125rem' }}>
                                {sug.best_send_times.recommended_hours}
                              </p>
                              <p style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>Recommended sending window</p>
                            </div>
                            <p style={{ fontSize: '0.775rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                              {sug.best_send_times.reasoning}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Trending Insights */}
                    {sug.trending_insights?.length > 0 && (
                      <div className="card" style={{ marginBottom: '1.25rem' }}>
                        <div className="card-header">
                          <h3><TrendingUp size={18} /> Trending Now</h3>
                          <span style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: '4px', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', fontWeight: 700 }}>LIVE WEB DATA</span>
                        </div>
                        <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {sug.trending_insights.map((t, i) => (
                            <div key={i} style={{
                              padding: '0.875rem', borderRadius: '10px',
                              background: 'rgba(59, 130, 246, 0.03)',
                              border: '1px solid rgba(59, 130, 246, 0.08)'
                            }}>
                              <p style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--color-text-primary)', marginBottom: '0.25rem' }}>{t.trend}</p>
                              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', marginBottom: '0.25rem' }}>{t.relevance}</p>
                              <p style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>Source: {t.source}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Content Tips */}
                    {sug.content_tips?.length > 0 && (
                      <div className="card" style={{ marginBottom: '1.25rem' }}>
                        <div className="card-header"><h3><Zap size={18} /> Content Tips</h3></div>
                        <div style={{ padding: '1.25rem' }}>
                          {sug.content_tips.map((tip, i) => (
                            <div key={i} style={{
                              display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                              padding: '0.5rem 0',
                              borderBottom: i < sug.content_tips.length - 1 ? '1px solid var(--color-border-light)' : 'none'
                            }}>
                              <CheckCircle size={14} style={{ color: '#22c55e', marginTop: 3, flexShrink: 0 }} />
                              <span style={{ fontSize: '0.825rem', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{tip}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Footer */}
                    <div style={{ textAlign: 'center', padding: '0.5rem', fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                      <Globe size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.25rem' }} />
                      Suggestions powered by web search + your campaign history · Generated {new Date(aiSuggestions.generated_at).toLocaleString()}
                    </div>
                  </>
                )
              })()}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* TAB: Best Send Times                            */}
      {/* ══════════════════════════════════════════════ */}
      {activeTab === 'send-times' && (
        <div>
          {!sendTimes && !sendTimesLoading && (
            <div className="card">
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <Timer size={48} style={{ margin: '0 auto 1rem', color: '#f59e0b', opacity: 0.6 }} />
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>Discover Your Best Send Times</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '1.25rem', maxWidth: 420, margin: '0 auto 1.25rem' }}>
                  Analyze when your audience opens emails most — broken down by day and hour.
                </p>
                <button className="btn btn-primary" onClick={fetchSendTimes} style={{
                  background: 'linear-gradient(135deg, #f59e0b, #f97316)', border: 'none',
                  padding: '0.625rem 1.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem'
                }}>
                  <Timer size={16} /> Analyze Send Times
                </button>
              </div>
            </div>
          )}

          {sendTimesLoading && (
            <div className="card">
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <div style={{ width: 48, height: 48, border: '3px solid var(--color-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
                <p style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>Analyzing send patterns...</p>
              </div>
            </div>
          )}

          {sendTimes && !sendTimesLoading && (() => {
            const { heatmap, best_slots, day_stats, hour_stats, ai_summary } = sendTimes
            const maxRate = Math.max(...heatmap.map(h => h.open_rate), 1)
            const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
            const hours = Array.from({ length: 24 }, (_, i) => i)

            // Build grid lookup
            const grid = {}
            heatmap.forEach(h => { grid[`${h.day}-${h.hour}`] = h })

            const cellColor = (rate) => {
              if (rate === 0) return 'var(--color-bg-secondary)'
              const intensity = Math.min(rate / maxRate, 1)
              return `rgba(34, 197, 94, ${0.1 + intensity * 0.7})`
            }

            return (
              <>
                {/* AI Recommendation */}
                {ai_summary && (
                  <div className="card" style={{ marginBottom: '1.25rem', background: 'rgba(245, 158, 11, 0.03)', border: '1px solid rgba(245, 158, 11, 0.12)' }}>
                    <div style={{ padding: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <Brain size={18} style={{ color: '#f59e0b' }} />
                        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>AI Recommendation</h3>
                        <span style={{ fontSize: '0.6rem', padding: '2px 8px', borderRadius: '4px', background: ai_summary.confidence === 'high' ? 'rgba(34,197,94,0.1)' : 'rgba(245,158,11,0.1)', color: ai_summary.confidence === 'high' ? '#22c55e' : '#f59e0b', fontWeight: 700 }}>
                          {ai_summary.confidence?.toUpperCase()} CONFIDENCE
                        </span>
                        <button className="btn btn-ghost btn-sm" onClick={fetchSendTimes} style={{ marginLeft: 'auto' }}><RefreshCw size={14} /></button>
                      </div>
                      <p style={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--color-text-secondary)', marginBottom: '0.75rem' }}>{ai_summary.recommendation}</p>
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        {ai_summary.best_days && (
                          <div>
                            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.375rem' }}>BEST DAYS</span>
                            <div style={{ display: 'flex', gap: '0.375rem' }}>
                              {ai_summary.best_days.map((d, i) => (
                                <span key={i} style={{ padding: '4px 10px', borderRadius: '6px', background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontWeight: 700, fontSize: '0.75rem' }}>{d}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {ai_summary.best_hours && (
                          <div>
                            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.375rem' }}>BEST HOURS</span>
                            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#6366f1' }}>{ai_summary.best_hours}</span>
                          </div>
                        )}
                        {ai_summary.avoid_times && (
                          <div>
                            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-muted)', display: 'block', marginBottom: '0.375rem' }}>AVOID</span>
                            <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#ef4444' }}>{ai_summary.avoid_times}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Heatmap Grid */}
                <div className="card" style={{ marginBottom: '1.25rem' }}>
                  <div className="card-header">
                    <h3><Timer size={20} /> Open Rate Heatmap</h3>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                      {heatmap.reduce((s, h) => s + h.sent, 0).toLocaleString()} emails analyzed
                    </span>
                  </div>
                  <div style={{ padding: '1.5rem', overflowX: 'auto' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: `60px repeat(24, 1fr)`, gap: '2px', minWidth: 700 }}>
                      {/* Header row */}
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, color: 'var(--color-text-muted)' }}></div>
                      {hours.map(h => (
                        <div key={h} style={{ fontSize: '0.6rem', fontWeight: 600, color: 'var(--color-text-muted)', textAlign: 'center', padding: '4px 0' }}>
                          {h % 3 === 0 ? `${h}:00` : ''}
                        </div>
                      ))}
                      {/* Day rows */}
                      {days.map(day => (
                        <>
                          <div key={`label-${day}`} style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center' }}>{day}</div>
                          {hours.map(hr => {
                            const cell = grid[`${day}-${hr}`]
                            const rate = cell?.open_rate || 0
                            const sent = cell?.sent || 0
                            return (
                              <div key={`${day}-${hr}`}
                                title={`${day} ${hr}:00 — Sent: ${sent}, Open Rate: ${rate}%`}
                                style={{
                                  height: 28, borderRadius: '4px',
                                  background: cellColor(rate),
                                  cursor: sent > 0 ? 'pointer' : 'default',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '0.55rem', fontWeight: 700,
                                  color: rate > maxRate * 0.5 ? 'white' : 'transparent',
                                  transition: 'transform 0.1s',
                                }}
                                onMouseEnter={e => { if (sent > 0) e.currentTarget.style.transform = 'scale(1.15)' }}
                                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                              >
                                {rate > 0 ? `${rate}%` : ''}
                              </div>
                            )
                          })}
                        </>
                      ))}
                    </div>
                    {/* Legend */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', justifyContent: 'center' }}>
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>Low</span>
                      {[0.15, 0.3, 0.5, 0.7, 0.85].map((v, i) => (
                        <div key={i} style={{ width: 20, height: 12, borderRadius: '2px', background: `rgba(34, 197, 94, ${0.1 + v * 0.7})` }} />
                      ))}
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>High Open Rate</span>
                    </div>
                  </div>
                </div>

                {/* Day + Hour Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
                  {/* Day Stats */}
                  <div className="card">
                    <div className="card-header"><h3>Opens by Day</h3></div>
                    <div style={{ padding: '1.25rem' }}>
                      {day_stats?.map((d, i) => {
                        const maxDaySent = Math.max(...(day_stats || []).map(x => x.sent), 1)
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-secondary)', width: 32 }}>{d.day}</span>
                            <div style={{ flex: 1, height: 24, background: 'var(--color-bg-primary)', borderRadius: '12px', overflow: 'hidden', position: 'relative' }}>
                              <div style={{
                                height: '100%', borderRadius: '12px',
                                background: d.open_rate >= 25 ? 'linear-gradient(90deg, #22c55e, #4ade80)' : d.open_rate >= 15 ? 'linear-gradient(90deg, #f59e0b, #fbbf24)' : 'linear-gradient(90deg, #ef4444, #f87171)',
                                width: `${(d.sent / maxDaySent) * 100}%`,
                                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '0.5rem',
                                minWidth: d.sent > 0 ? 50 : 0,
                              }}>
                                <span style={{ fontSize: '0.65rem', fontWeight: 700, color: 'white' }}>{d.open_rate}%</span>
                              </div>
                            </div>
                            <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', width: 45, textAlign: 'right' }}>{d.sent} sent</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Hour Stats */}
                  <div className="card">
                    <div className="card-header"><h3>Opens by Hour</h3></div>
                    <div style={{ padding: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 120 }}>
                        {hour_stats?.map((h, i) => {
                          const maxHrSent = Math.max(...(hour_stats || []).map(x => x.sent), 1)
                          const barH = h.sent > 0 ? Math.max((h.sent / maxHrSent) * 100, 8) : 4
                          return (
                            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}
                              title={`${h.label} — Sent: ${h.sent}, Open Rate: ${h.open_rate}%`}
                            >
                              <div style={{
                                width: '100%', borderRadius: '3px 3px 0 0',
                                height: `${barH}%`,
                                background: h.open_rate >= 25 ? '#22c55e' : h.open_rate >= 15 ? '#f59e0b' : h.sent > 0 ? '#94a3b8' : 'var(--color-bg-secondary)',
                                transition: 'height 0.3s',
                              }} />
                              {i % 3 === 0 && <span style={{ fontSize: '0.5rem', color: 'var(--color-text-muted)' }}>{h.hour}</span>}
                            </div>
                          )
                        })}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                        <span style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>12am</span>
                        <span style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>12pm</span>
                        <span style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>11pm</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top 5 Best Slots */}
                {best_slots?.length > 0 && (
                  <div className="card">
                    <div className="card-header"><h3><Star size={18} /> Top Performing Time Slots</h3></div>
                    <div style={{ padding: '1.25rem', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem' }}>
                      {best_slots.slice(0, 5).map((s, i) => (
                        <div key={i} style={{
                          padding: '1rem', borderRadius: '10px', textAlign: 'center',
                          background: i === 0 ? 'rgba(34,197,94,0.06)' : 'var(--color-bg-primary)',
                          border: i === 0 ? '1px solid rgba(34,197,94,0.2)' : '1px solid var(--color-border-light)',
                        }}>
                          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: '0.375rem' }}>
                            #{i + 1}
                          </div>
                          <div style={{ fontWeight: 800, fontSize: '1rem', color: '#22c55e' }}>{s.open_rate}%</div>
                          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-primary)', marginTop: '0.25rem' }}>
                            {s.day} {s.hour}:00
                          </div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)' }}>{s.sent} emails</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )
          })()}
        </div>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* TAB: Ask AI                                    */}
      {/* ══════════════════════════════════════════════ */}
      {activeTab === 'ask-ai' && (
        <div>
          <div className="card" style={{ marginBottom: '1.25rem' }}>
            <div style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <MessageCircle size={20} style={{ color: '#8b5cf6' }} />
                <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Ask AI About Your Campaigns</h3>
              </div>

              {/* Chat Messages */}
              <div style={{
                minHeight: 200, maxHeight: 450, overflowY: 'auto',
                marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem',
                padding: chatMessages.length === 0 ? '0' : '0.75rem',
                background: chatMessages.length > 0 ? 'var(--color-bg-primary)' : 'transparent',
                borderRadius: '10px',
              }}>
                {chatMessages.length === 0 && (
                  <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <Brain size={40} style={{ margin: '0 auto 0.75rem', color: '#8b5cf6', opacity: 0.4 }} />
                    <p style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--color-text-muted)', marginBottom: '1.25rem' }}>
                      Ask anything about your email campaigns
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
                      {[
                        'Which campaign has the best open rate?',
                        'Compare my gmail vs outlook deliverability',
                        'What is my overall reply rate?',
                        'Which subject lines performed best?',
                        'How are my bounce rates trending?',
                        'Summarize my campaign performance',
                      ].map((q, i) => (
                        <button key={i} onClick={() => { setChatInput(q) }} style={{
                          padding: '0.5rem 0.875rem', borderRadius: '20px', fontSize: '0.775rem',
                          background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                          color: 'var(--color-text-secondary)', cursor: 'pointer', fontWeight: 500,
                          transition: 'all 0.15s',
                        }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.08)'; e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.color = '#8b5cf6' }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-bg-secondary)'; e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-text-secondary)' }}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {chatMessages.map((msg, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  }}>
                    <div style={{
                      maxWidth: '80%',
                      padding: '0.75rem 1rem',
                      borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      background: msg.role === 'user' ? 'linear-gradient(135deg, #8b5cf6, #6366f1)' : 'var(--color-bg-secondary)',
                      color: msg.role === 'user' ? 'white' : 'var(--color-text-primary)',
                      fontSize: '0.85rem', lineHeight: 1.6,
                      border: msg.role === 'ai' ? '1px solid var(--color-border)' : 'none',
                    }}>
                      {msg.role === 'ai' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
                          <Sparkles size={13} style={{ color: '#8b5cf6' }} />
                          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#8b5cf6' }}>AI</span>
                        </div>
                      )}
                      <div style={{ whiteSpace: 'pre-wrap' }}
                        dangerouslySetInnerHTML={{
                          __html: msg.role === 'ai'
                            ? msg.content
                                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                .replace(/\n- /g, '\n• ')
                                .replace(/\n/g, '<br/>')
                            : msg.content
                        }}
                      />
                    </div>
                  </div>
                ))}

                {chatLoading && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {[0, 1, 2].map(i => (
                        <div key={i} style={{
                          width: 8, height: 8, borderRadius: '50%', background: '#8b5cf6',
                          animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                          opacity: 0.5,
                        }} />
                      ))}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Analyzing your data...</span>
                  </div>
                )}
              </div>

              {/* Input */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAskAi() }}
                  placeholder="Ask about your campaigns, deliverability, engagement..."
                  style={{
                    flex: 1, padding: '0.75rem 1rem', borderRadius: '12px',
                    border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)',
                    fontSize: '0.875rem', color: 'var(--color-text-primary)',
                    outline: 'none',
                  }}
                  onFocus={e => e.currentTarget.style.borderColor = '#8b5cf6'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--color-border)'}
                />
                <button
                  onClick={handleAskAi}
                  disabled={!chatInput.trim() || chatLoading}
                  style={{
                    padding: '0.75rem 1.25rem', borderRadius: '12px', border: 'none',
                    background: chatInput.trim() ? 'linear-gradient(135deg, #8b5cf6, #6366f1)' : 'var(--color-bg-secondary)',
                    color: chatInput.trim() ? 'white' : 'var(--color-text-muted)',
                    cursor: chatInput.trim() ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', gap: '0.375rem', fontWeight: 600, fontSize: '0.85rem',
                    transition: 'all 0.15s',
                  }}
                >
                  <Send size={16} /> Ask
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats Reference */}
          {dashboard && (
            <div className="card">
              <div className="card-header"><h3><BarChart3 size={16} /> Quick Reference</h3></div>
              <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem' }}>
                {[
                  { label: 'Total Sent', value: fmt(kpis.total_sent), color: '#6366f1' },
                  { label: 'Open Rate', value: pct(kpis.avg_open_rate), color: '#22c55e' },
                  { label: 'Click Rate', value: pct(kpis.avg_click_rate), color: '#3b82f6' },
                  { label: 'Replies', value: fmt(kpis.total_replies), color: '#a855f7' },
                  { label: 'Bounce Rate', value: pct(kpis.avg_bounce_rate), color: '#f59e0b' },
                ].map((s, i) => (
                  <div key={i} style={{ textAlign: 'center', padding: '0.5rem' }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', fontWeight: 500 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* TAB: Subject Line Scorer                        */}
      {/* ══════════════════════════════════════════════ */}
      {activeTab === 'subject-scorer' && (
        <div className="reports-grid" style={{ display: 'grid', gap: '1.25rem' }}>
          {/* Input Card */}
          <div className="card" style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Type size={20} style={{ color: 'var(--color-primary)' }} />
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Subject Line Scorer</h3>
              <span style={{ fontSize: '0.7rem', background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#fff', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>AI</span>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
              Enter a subject line to get an AI-powered score, predicted open rate, and improvement suggestions based on your past campaign performance.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <input
                type="text"
                value={subjectInput}
                onChange={e => setSubjectInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && scoreSubjectLine()}
                placeholder="Type your subject line here..."
                style={{ flex: 1, padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: '0.95rem', outline: 'none' }}
              />
              <button
                onClick={scoreSubjectLine}
                disabled={subjectLoading || !subjectInput.trim()}
                style={{ padding: '0.75rem 1.5rem', background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', opacity: subjectLoading || !subjectInput.trim() ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {subjectLoading ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={16} />}
                {subjectLoading ? 'Scoring...' : 'Score'}
              </button>
            </div>
          </div>

          {/* Results */}
          {subjectScore && !subjectScore.error && (
            <>
              {/* Score + Grade + Predicted Open Rate */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.25rem' }}>
                {/* Score Donut */}
                <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
                  <h4 style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>Score</h4>
                  <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto' }}>
                    <svg viewBox="0 0 36 36" style={{ width: 120, height: 120, transform: 'rotate(-90deg)' }}>
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none" stroke="var(--color-border)" strokeWidth="3" />
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke={subjectScore.score >= 70 ? '#22c55e' : subjectScore.score >= 40 ? '#f59e0b' : '#ef4444'}
                        strokeWidth="3"
                        strokeDasharray={`${subjectScore.score}, 100`} />
                    </svg>
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '1.5rem', fontWeight: 700, color: subjectScore.score >= 70 ? '#22c55e' : subjectScore.score >= 40 ? '#f59e0b' : '#ef4444' }}>
                      {subjectScore.score}
                    </div>
                  </div>
                </div>

                {/* Grade */}
                <div className="card" style={{ padding: '1.5rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <h4 style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>Grade</h4>
                  <div style={{
                    width: 80, height: 80, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '2rem', fontWeight: 800,
                    background: subjectScore.grade?.startsWith('A') ? '#dcfce7' : subjectScore.grade?.startsWith('B') ? '#fef3c7' : '#fee2e2',
                    color: subjectScore.grade?.startsWith('A') ? '#16a34a' : subjectScore.grade?.startsWith('B') ? '#d97706' : '#dc2626',
                  }}>
                    {subjectScore.grade}
                  </div>
                </div>

                {/* Predicted Open Rate */}
                <div className="card" style={{ padding: '1.5rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <h4 style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>Predicted Open Rate</h4>
                  <div style={{ fontSize: '2.25rem', fontWeight: 700, color: 'var(--color-primary)' }}>
                    {subjectScore.predicted_open_rate}%
                  </div>
                  {subjectScore.data_context?.average_open_rate > 0 && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>
                      Your avg: {subjectScore.data_context.average_open_rate}%
                    </span>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="card" style={{ padding: '1.5rem' }}>
                <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Summary</h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                  {subjectScore.summary}
                </p>
              </div>

              {/* Analysis Breakdown */}
              {subjectScore.analysis && (
                <div className="card" style={{ padding: '1.5rem' }}>
                  <h4 style={{ margin: '0 0 1rem', fontSize: '0.95rem' }}>Analysis Breakdown</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.75rem' }}>
                    {Object.entries(subjectScore.analysis).map(([key, val]) => (
                      <div key={key} style={{ padding: '0.75rem 1rem', background: 'var(--color-bg-secondary)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</span>
                          {val.detected !== undefined && (
                            <span style={{ fontSize: '0.7rem', padding: '2px 6px', borderRadius: 4, background: val.detected ? '#dcfce7' : '#f3f4f6', color: val.detected ? '#16a34a' : '#6b7280' }}>
                              {val.detected ? 'Yes' : 'No'}
                            </span>
                          )}
                          {val.value !== undefined && (
                            <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{val.value}</span>
                          )}
                        </div>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{val.verdict}</p>
                        {val.found && val.found.length > 0 && (
                          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                            {val.found.map((w, i) => (
                              <span key={i} style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: 4, background: '#fee2e2', color: '#dc2626' }}>{w}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strengths & Weaknesses */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <div className="card" style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <ThumbsUp size={18} style={{ color: '#22c55e' }} />
                    <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Strengths</h4>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', listStyle: 'disc' }}>
                    {(subjectScore.strengths || []).map((s, i) => (
                      <li key={i} style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.4rem', lineHeight: 1.5 }}>{s}</li>
                    ))}
                  </ul>
                </div>
                <div className="card" style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <ThumbsDown size={18} style={{ color: '#ef4444' }} />
                    <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Weaknesses</h4>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem', listStyle: 'disc' }}>
                    {(subjectScore.weaknesses || []).map((w, i) => (
                      <li key={i} style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.4rem', lineHeight: 1.5 }}>{w}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* AI Suggestions */}
              {subjectScore.suggestions && subjectScore.suggestions.length > 0 && (
                <div className="card" style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                    <Lightbulb size={18} style={{ color: '#f59e0b' }} />
                    <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Improved Suggestions</h4>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {subjectScore.suggestions.map((sug, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: 'var(--color-warning-bg)', border: '1px solid rgba(217, 119, 6, 0.25)', borderRadius: 8, cursor: 'pointer' }}
                        onClick={() => setSubjectInput(sug)}
                      >
                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#d97706', minWidth: 20 }}>#{i + 1}</span>
                        <span style={{ fontSize: '0.9rem', color: '#92400e', flex: 1 }}>{sug}</span>
                        <span style={{ fontSize: '0.7rem', color: '#d97706' }}>Click to test</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Error state */}
          {subjectScore?.error && (
            <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <AlertTriangle size={32} style={{ color: '#ef4444', margin: '0 auto 0.75rem' }} />
              <p style={{ color: '#ef4444', fontSize: '0.9rem' }}>Failed to score subject line. Please try again.</p>
            </div>
          )}

          {/* Empty state */}
          {!subjectScore && !subjectLoading && (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <Type size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
              <p style={{ fontSize: '0.95rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>Enter a subject line above to get started</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>AI will analyze your subject line against your past campaign performance data</p>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* TAB: Smart Segments                             */}
      {/* ══════════════════════════════════════════════ */}
      {activeTab === 'smart-segments' && (
        <div className="reports-grid" style={{ display: 'grid', gap: '1.25rem' }}>
          {/* Header */}
          <div className="card" style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Crosshair size={20} style={{ color: 'var(--color-primary)' }} />
                  <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Smart Segmentation</h3>
                  <span style={{ fontSize: '0.7rem', background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#fff', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>AI</span>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: 0 }}>
                  AI analyzes your contact engagement patterns and suggests targeted audience segments.
                </p>
              </div>
              <button
                onClick={fetchSegments}
                disabled={segLoading}
                style={{ padding: '0.625rem 1.5rem', background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', opacity: segLoading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                {segLoading ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={16} />}
                {segLoading ? 'Analyzing...' : segments ? 'Regenerate' : 'Generate Segments'}
              </button>
            </div>
          </div>

          {/* Loading */}
          {segLoading && (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <div style={{ width: 48, height: 48, border: '3px solid var(--color-border)', borderTopColor: '#8b5cf6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
              <p style={{ fontWeight: 600 }}>Analyzing contact engagement patterns...</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>This may take a few seconds</p>
            </div>
          )}

          {/* Results */}
          {!segLoading && segments && segments.segments?.length > 0 && (
            <>
              {/* Summary */}
              <div className="card" style={{ padding: '1.5rem' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{segments.summary}</p>
              </div>

              {/* Data overview */}
              {segments.data_context && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                  <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Total Contacts</span>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-primary)' }}>{segments.data_context.total_contacts}</div>
                  </div>
                  <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>High Engagement</span>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#22c55e' }}>{segments.data_context.engagement_levels?.high || 0}</div>
                  </div>
                  <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Low Engagement</span>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>{segments.data_context.engagement_levels?.low || 0}</div>
                  </div>
                  <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Bounced</span>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ef4444' }}>{segments.data_context.bounced || 0}</div>
                  </div>
                </div>
              )}

              {/* Segment Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                {segments.segments.map((seg, i) => {
                  const priorityColors = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' }
                  return (
                    <div key={i} className="card" style={{ padding: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                        <h4 style={{ margin: 0, fontSize: '0.95rem' }}>{seg.name}</h4>
                        <span style={{
                          fontSize: '0.65rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600, textTransform: 'uppercase',
                          background: `${priorityColors[seg.priority] || '#6366f1'}15`,
                          color: priorityColors[seg.priority] || '#6366f1'
                        }}>
                          {seg.priority} priority
                        </span>
                      </div>
                      <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', lineHeight: 1.5, marginBottom: '0.75rem' }}>{seg.description}</p>

                      {/* Criteria tags */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.75rem' }}>
                        {seg.criteria?.engagement_level && seg.criteria.engagement_level !== 'any' && (
                          <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 12, background: '#ede9fe', color: '#7c3aed' }}>
                            {seg.criteria.engagement_level} engagement
                          </span>
                        )}
                        {seg.criteria?.industry && seg.criteria.industry !== 'any' && (
                          <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 12, background: '#dbeafe', color: '#2563eb' }}>
                            {seg.criteria.industry}
                          </span>
                        )}
                        {seg.criteria?.source && seg.criteria.source !== 'any' && (
                          <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 12, background: '#dcfce7', color: '#16a34a' }}>
                            {seg.criteria.source}
                          </span>
                        )}
                        {seg.criteria?.location && seg.criteria.location !== 'any' && (
                          <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 12, background: '#fef3c7', color: '#d97706' }}>
                            {seg.criteria.location}
                          </span>
                        )}
                        {seg.criteria?.custom_filters?.map((f, fi) => (
                          <span key={fi} style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 12, background: '#f3f4f6', color: '#6b7280' }}>
                            {f}
                          </span>
                        ))}
                      </div>

                      {/* Estimated size */}
                      {seg.estimated_size && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
                          <Users size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                          Est. size: <strong>{seg.estimated_size}</strong>
                        </div>
                      )}

                      {/* Recommended action */}
                      <div style={{ padding: '0.625rem 0.75rem', borderRadius: 8, background: 'rgba(99, 102, 241, 0.05)', borderLeft: '3px solid #6366f1', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6366f1', display: 'block', marginBottom: '0.2rem' }}>Recommended Action</span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{seg.recommended_action}</span>
                      </div>

                      {/* Expected impact */}
                      {seg.expected_impact && (
                        <div style={{ fontSize: '0.775rem', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <TrendingUp size={13} />
                          {seg.expected_impact}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Overall Insights */}
              {segments.overall_insights?.length > 0 && (
                <div className="card" style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <Lightbulb size={18} style={{ color: '#f59e0b' }} />
                    <h4 style={{ margin: 0, fontSize: '0.95rem' }}>Key Insights</h4>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                    {segments.overall_insights.map((ins, i) => (
                      <li key={i} style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.4rem', lineHeight: 1.5 }}>{ins}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* Empty state */}
          {!segLoading && !segments && (
            <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
              <Crosshair size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
              <p style={{ fontSize: '0.95rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>Click "Generate Segments" to get AI-powered audience suggestions</p>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>AI will analyze contact engagement, demographics, and campaign performance</p>
            </div>
          )}

          {/* Error */}
          {!segLoading && segments?.error && !segments.segments?.length && (
            <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <AlertTriangle size={32} style={{ color: '#ef4444', margin: '0 auto 0.75rem' }} />
              <p style={{ color: '#ef4444', fontSize: '0.9rem' }}>Failed to generate segments. Please try again.</p>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* TAB: Engagement Over Time                      */}
      {/* ══════════════════════════════════════════════ */}
      {activeTab === 'engagement' && (
        <div className="card">
          <div className="card-header">
            <h3><Activity size={20} /> Engagement Over Time ({dateRange})</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => handleExport('campaign_stats')}><Download size={16} /> CSV</button>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {engagement?.data?.length > 0 ? (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Sent</th>
                      <th>Opens</th>
                      <th>Clicks</th>
                      <th>Bounces</th>
                      <th>Open Rate</th>
                      <th>Click Rate</th>
                      <th>Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {engagement.data.map((row, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 500 }}>{row.period}</td>
                        <td>{fmt(row.sent)}</td>
                        <td>{fmt(row.opens)}</td>
                        <td>{fmt(row.clicks)}</td>
                        <td>{row.bounces > 0 ? <span style={{ color: '#ef4444', fontWeight: 600 }}>{fmt(row.bounces)}</span> : '0'}</td>
                        <td><span className={`rate ${rateClass(row.open_rate)}`}>{row.open_rate}%</span></td>
                        <td><span className={`rate ${rateClass(row.click_rate)}`}>{row.click_rate}%</span></td>
                        <td>
                          <div className="performance-bar">
                            <div className="performance-fill" style={{ width: `${Math.min(row.open_rate, 100)}%`, background: rateColor(row.open_rate) }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                <Activity size={48} style={{ margin: '0 auto 1rem', opacity: 0.4 }} />
                <p>No engagement data for this time range</p>
              </div>
            )}

            {/* Visual bar breakdown */}
            {engagement?.data?.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>Daily Volume</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {engagement.data.slice(-14).map((row, i) => {
                    const maxSent = Math.max(...engagement.data.map(d => d.sent), 1)
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', minWidth: '80px', fontWeight: 500 }}>{row.period.slice(5)}</span>
                        <div style={{ flex: 1, height: '24px', background: 'var(--color-bg-primary)', borderRadius: '12px', overflow: 'hidden', position: 'relative' }}>
                          <div style={{
                            height: '100%', borderRadius: '12px',
                            background: 'linear-gradient(90deg, #6366f1, #818cf8)',
                            width: `${(row.sent / maxSent) * 100}%`,
                            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '0.5rem',
                            transition: 'width 0.5s ease', minWidth: row.sent > 0 ? '40px' : '0'
                          }}>
                            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'white' }}>{row.sent}</span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* TAB: Account Health                            */}
      {/* ══════════════════════════════════════════════ */}
      {activeTab === 'accounts' && (
        <div className="card">
          <div className="card-header">
            <h3><Shield size={20} /> Email Account Health</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => handleExport('campaign_stats')}><Download size={16} /> CSV</button>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {accountHealth?.accounts?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {accountHealth.accounts.map((acct, i) => (
                  <div key={i} className="card" style={{ border: '1px solid var(--color-border)' }}>
                    <div style={{ padding: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{
                            width: 40, height: 40, borderRadius: '10px',
                            background: acct.is_active ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: acct.is_active ? '#22c55e' : '#ef4444'
                          }}>
                            <Mail size={20} />
                          </div>
                          <div>
                            <p style={{ fontWeight: 600, color: 'var(--color-text-primary)', fontSize: '0.95rem' }}>{acct.name}</p>
                            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{acct.email}</p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <span className={`status-badge ${acct.is_active ? 'active' : 'stopped'}`}>
                            {acct.is_active ? 'Active' : 'Inactive'}
                          </span>
                          {acct.is_verified && <span className="status-badge completed">Verified</span>}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '1rem' }}>
                        {[
                          { label: 'Sent', value: fmt(acct.total_sent), color: '#6366f1' },
                          { label: 'Delivered', value: fmt(acct.total_delivered), color: '#22c55e' },
                          { label: 'Bounced', value: fmt(acct.total_bounced), color: '#ef4444' },
                          { label: 'Opens', value: fmt(acct.total_opens), color: '#8b5cf6' },
                          { label: 'Delivery %', value: `${acct.delivery_rate}%`, color: rateColor(acct.delivery_rate) },
                          { label: 'Open %', value: `${acct.open_rate}%`, color: rateColor(acct.open_rate) },
                        ].map((m, j) => (
                          <div key={j} style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '1.25rem', fontWeight: 700, color: m.color }}>{m.value}</p>
                            <p style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', fontWeight: 500 }}>{m.label}</p>
                          </div>
                        ))}
                      </div>

                      {acct.pending_queue > 0 && (
                        <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', background: 'rgba(245, 158, 11, 0.08)', borderRadius: '8px', fontSize: '0.8rem', color: '#d97706', fontWeight: 500 }}>
                          <Clock size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.375rem' }} />
                          {acct.pending_queue} emails pending in queue
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                <Shield size={48} style={{ margin: '0 auto 1rem', opacity: 0.4 }} />
                <p>No email accounts configured</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* TAB: Reply Analytics                           */}
      {/* ══════════════════════════════════════════════ */}
      {activeTab === 'replies' && (
        <div>
          {/* Reply KPIs */}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '1.25rem' }}>
            {[
              { label: 'Total Replies', value: fmt(replyAnalytics?.total_replies), icon: Reply, color: '#a855f7' },
              { label: 'Reply Rate', value: `${replyAnalytics?.reply_rate ?? 0}%`, icon: Target, color: '#6366f1' },
              { label: 'Avg Response Time', value: replyAnalytics?.avg_response_time_hours ? `${replyAnalytics.avg_response_time_hours}h` : 'N/A', icon: Clock, color: '#3b82f6' },
              { label: 'Positive Replies', value: fmt(replyAnalytics?.sentiment_breakdown?.positive), icon: TrendingUp, color: '#22c55e' },
            ].map((stat, i) => (
              <div key={i} className="stat-card" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: '0.75rem' }}>
                  <div className="stat-icon" style={{ background: `${stat.color}15`, color: stat.color }}><stat.icon size={20} /></div>
                </div>
                <span className="stat-label">{stat.label}</span>
                <span className="stat-value">{stat.value}</span>
              </div>
            ))}
          </div>

          <div className="charts-grid">
            {/* Sentiment Breakdown */}
            <div className="card">
              <div className="card-header"><h3><Zap size={20} /> Sentiment Breakdown</h3></div>
              <div style={{ padding: '1.5rem' }}>
                {(() => {
                  const sb = replyAnalytics?.sentiment_breakdown || {}
                  const total = (sb.positive || 0) + (sb.negative || 0) + (sb.neutral || 0)
                  const sentiments = [
                    { label: 'Positive', value: sb.positive || 0, color: '#22c55e' },
                    { label: 'Neutral', value: sb.neutral || 0, color: '#f59e0b' },
                    { label: 'Negative', value: sb.negative || 0, color: '#ef4444' },
                  ]
                  return total > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {sentiments.map((s, i) => (
                        <div key={i}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.375rem', fontSize: '0.8rem' }}>
                            <span style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>{s.label}</span>
                            <span style={{ fontWeight: 700, color: s.color }}>{s.value} ({total > 0 ? Math.round(s.value / total * 100) : 0}%)</span>
                          </div>
                          <div style={{ height: '8px', background: 'var(--color-bg-secondary)', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', borderRadius: '4px', background: s.color, width: `${total > 0 ? (s.value / total) * 100 : 0}%`, transition: 'width 0.5s ease' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '2rem', fontSize: '0.875rem' }}>No sentiment data yet</p>
                  )
                })()}
              </div>
            </div>

            {/* Tag Breakdown */}
            <div className="card">
              <div className="card-header"><h3><Layers size={20} /> Reply Tags</h3></div>
              <div style={{ padding: '1.5rem' }}>
                {(() => {
                  const tags = replyAnalytics?.tag_breakdown || {}
                  const entries = Object.entries(tags).sort((a, b) => b[1] - a[1])
                  const tagColors = ['#6366f1', '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#a855f7', '#ec4899']
                  return entries.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {entries.map(([tag, count], i) => (
                        <div key={tag} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.625rem 0', borderBottom: '1px solid var(--color-border-light)' }}>
                          <span className="status-badge" style={{ background: `${tagColors[i % tagColors.length]}15`, color: tagColors[i % tagColors.length] }}>{tag}</span>
                          <span style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>{count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '2rem', fontSize: '0.875rem' }}>No tags classified yet</p>
                  )
                })()}
              </div>
            </div>
          </div>

          <button className="btn btn-ghost" style={{ marginTop: '1rem' }} onClick={() => handleExport('replies')}>
            <Download size={16} /> Export Reply Data (CSV)
          </button>
        </div>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* TAB: Top Contacts                              */}
      {/* ══════════════════════════════════════════════ */}
      {activeTab === 'contacts' && (
        <div className="card">
          <div className="card-header">
            <h3><Users size={20} /> Most Engaged Contacts</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => handleExport('contacts')}><Download size={16} /> CSV</button>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {topContacts?.contacts?.length > 0 ? (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Email</th>
                      <th>Emails Received</th>
                      <th>Opens</th>
                      <th>Clicks</th>
                      <th>Campaigns</th>
                      <th>Replied</th>
                      <th>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topContacts.contacts.map((c, i) => (
                      <tr key={i}>
                        <td style={{ color: 'var(--color-text-muted)', fontWeight: 700, fontSize: '0.8rem' }}>{i + 1}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: '8px',
                              background: `hsl(${(c.email || '').charCodeAt(0) * 7 % 360}, 60%, 95%)`,
                              color: `hsl(${(c.email || '').charCodeAt(0) * 7 % 360}, 70%, 40%)`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontWeight: 700, fontSize: '0.75rem', flexShrink: 0
                            }}>
                              {(c.email || '?')[0].toUpperCase()}
                            </div>
                            <span style={{ fontWeight: 500 }}>{c.email}</span>
                          </div>
                        </td>
                        <td>{fmt(c.emails_received)}</td>
                        <td style={{ color: '#8b5cf6', fontWeight: 600 }}>{fmt(c.total_opens)}</td>
                        <td style={{ color: '#3b82f6', fontWeight: 600 }}>{fmt(c.total_clicks)}</td>
                        <td>{c.campaigns_count}</td>
                        <td>
                          {c.replied
                            ? <span className="status-badge completed" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}><CheckCircle size={13} /> Yes</span>
                            : <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                          }
                        </td>
                        <td>
                          <span style={{
                            fontWeight: 800, fontSize: '0.9rem',
                            color: c.engagement_score >= 20 ? '#22c55e' : c.engagement_score >= 5 ? '#f59e0b' : '#94a3b8'
                          }}>
                            {c.engagement_score}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                <Users size={48} style={{ margin: '0 auto 1rem', opacity: 0.4 }} />
                <p>No engagement data yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════ */}
      {/* TAB: Deliverability by Domain                  */}
      {/* ══════════════════════════════════════════════ */}
      {activeTab === 'domains' && (
        <div className="card">
          <div className="card-header">
            <h3><Globe size={20} /> Deliverability by Domain</h3>
            <button className="btn btn-ghost btn-sm" onClick={() => handleExport('deliverability')}><Download size={16} /> CSV</button>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {domainData?.domains?.length > 0 ? (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Sent</th>
                      <th>Delivered</th>
                      <th>Bounced</th>
                      <th>Opens</th>
                      <th>Bounce Rate</th>
                      <th>Open Rate</th>
                      <th>Health</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domainData.domains.map((d, i) => (
                      <tr key={i}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Globe size={16} style={{ color: '#6366f1', flexShrink: 0 }} />
                            <span className="campaign-name">{d.domain}</span>
                          </div>
                        </td>
                        <td>{fmt(d.sent)}</td>
                        <td style={{ color: '#22c55e', fontWeight: 600 }}>{fmt(d.delivered)}</td>
                        <td>{d.bounced > 0 ? <span style={{ color: '#ef4444', fontWeight: 600 }}>{fmt(d.bounced)}</span> : '0'}</td>
                        <td>{fmt(d.opens)}</td>
                        <td><span className={`rate ${d.bounce_rate <= 2 ? 'good' : d.bounce_rate <= 5 ? 'medium' : 'low'}`}>{d.bounce_rate}%</span></td>
                        <td><span className={`rate ${rateClass(d.open_rate)}`}>{d.open_rate}%</span></td>
                        <td>
                          <div className="performance-bar" style={{ width: '80px' }}>
                            <div className="performance-fill" style={{
                              width: `${100 - Math.min(d.bounce_rate * 5, 100)}%`,
                              background: d.bounce_rate <= 2 ? '#22c55e' : d.bounce_rate <= 5 ? '#f59e0b' : '#ef4444'
                            }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                <Globe size={48} style={{ margin: '0 auto 1rem', opacity: 0.4 }} />
                <p>No domain data available yet</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Reports
